/**
 * SOVRANOVA — SignaturePad (shared)
 *
 * A small draw-to-sign pad. Pointer events cover mouse, touch and stylus. The
 * value handed back is a compact PNG data URL — or null until someone draws.
 * A drawn signature is a materially stronger consent record than a tap: it is
 * deliberate, personal, and hard to claim was accidental.
 *
 * Extracted from ConsentFlow (19 Aug) so the couples-session agreement can use
 * the same pad. `tone` themes it: 'dark' (capture flow) or 'light' (couples
 * card on cream). Behaviour is identical in both.
 */
import { useRef } from 'react'

const TONES = {
  dark: {
    stroke: '#e8eef4',
    canvas: { background:'rgba(0,0,0,.3)', border:'1px dashed rgba(255,255,255,.25)' },
    hint:   { color:'rgba(240,244,248,.5)' },
    clear:  { color:'rgba(240,244,248,.55)' },
  },
  light: {
    stroke: '#2e2b25',
    canvas: { background:'#fff', border:'1px dashed rgba(0,0,0,.28)' },
    hint:   { color:'var(--mid-grey,#9a948a)' },
    clear:  { color:'var(--mid-grey,#6f6a61)' },
  },
}

export function SignaturePad({ value, onChange, tone = 'dark' }) {
  const canvasRef  = useRef(null)
  const drawingRef = useRef(false)
  const dirtyRef   = useRef(false)
  const t = TONES[tone] || TONES.dark

  const pos = (e) => {
    const c = canvasRef.current
    const r = c.getBoundingClientRect()
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) }
  }
  const start = (e) => {
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = pos(e)
    ctx.strokeStyle = t.stroke; ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'
    ctx.beginPath(); ctx.moveTo(x, y)
    drawingRef.current = true
    try { canvasRef.current.setPointerCapture(e.pointerId) } catch { /* noop */ }
  }
  const move = (e) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = pos(e)
    ctx.lineTo(x, y); ctx.stroke()
    dirtyRef.current = true
  }
  const end = () => {
    if (!drawingRef.current) return
    drawingRef.current = false
    if (dirtyRef.current) onChange(canvasRef.current.toDataURL('image/png'))
  }
  const clear = () => {
    const c = canvasRef.current
    c.getContext('2d').clearRect(0, 0, c.width, c.height)
    dirtyRef.current = false
    onChange(null)
  }

  return (
    <div style={sp.wrap}>
      <canvas
        ref={canvasRef}
        width={560} height={140}
        style={{ ...sp.canvas, ...t.canvas }}
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
      />
      <div style={sp.row}>
        <span style={{ ...sp.hint, ...t.hint }}>{value ? '✓ Signed' : 'Sign above with your finger'}</span>
        <button type="button" style={{ ...sp.clear, ...t.clear }} onClick={clear}>Clear</button>
      </div>
    </div>
  )
}

const sp = {
  wrap:   { marginTop:'0.45rem', marginBottom:'0.2rem' },
  canvas: { width:'100%', height:110, display:'block', borderRadius:'0.6rem', touchAction:'none' },
  row:    { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'0.3rem' },
  hint:   { fontSize:'0.75rem' },
  clear:  { background:'transparent', border:'none', fontSize:'0.78rem', cursor:'pointer', padding:'0.2rem 0.3rem' },
}
